import { Injectable } from '@angular/core';
import { ID } from '@datorama/akita';
import { NgEntityService } from '@datorama/akita-ng-entity-service';
import { translate } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { differenceInDays } from 'date-fns';
import { tap } from 'rxjs/operators';

import { LogBookEntry, Parameter } from '../../model/logbook-entry';
import { MessageType } from '../../model/logbook-notification-types';
import { HttpOptions, UpdateActionType } from '../../service/generic-akita-http.service';
import { WebsocketService } from '../../service/websocket.service';
import { MissionLogBookState, MissionLogBookStore } from '../state/mission-log-book.store';
import { AkitaMissionLogBookHttpService } from './akita-mission-log-book.http.service';
@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class MissionLogBookService extends NgEntityService<MissionLogBookState> {

  constructor(
    protected store: MissionLogBookStore,
    private missionLogBookHttpService: AkitaMissionLogBookHttpService,
    private webSocketService: WebsocketService
  ) {
    super(store);

    this.webSocketService.getDataStream('LogBookEntry')
      .pipe(untilDestroyed(this))
      .subscribe((data: LogBookEntry[]) => this.store.upsertMany(data));
  }
  updateEntitiesUrl(newUrl: string): void {
    this.missionLogBookHttpService.setEntitiesUrl(newUrl);
  }

  getAll() {
    this.missionLogBookHttpService.getAll().subscribe((logBooks) => this.store.upsertMany(logBooks));
  }

  getTranslation(message: MessageType, mesageParams: Parameter[] = [], friendlyName?: string) {
    const params: any = mesageParams.reduce((acc, cur) => {
      acc[cur.name] = cur.value;
      return acc;
    }, {});

    params.intervalTimespanUnit = translate(params.intervalTimespanUnit);
    params.comparisonTimespanUnit = translate(params.comparisonTimespanUnit);
    params.alertConditionOperator = translate(params.alertConditionOperator);
    params.reasonAlert = translate(params.reasonAlert);

    params.friendlyName = friendlyName;

    return translate(message, params);
  }

  getMissionLogBookById(id: ID) {
    this.missionLogBookHttpService.loadById(id).subscribe((mission) => this.store.add(mission));
  }

  getMissionLogBookByIdObs(id: ID) {
    return this.missionLogBookHttpService.loadById(id).pipe(tap((mission) => this.store.add(mission)));
  }

  getForPage(page: number) {
    this.store.setLoading(true);

    const limit = 50;
    const offset = (page - 1) * limit;

    this.missionLogBookHttpService.getNextXItems(offset, limit).subscribe(
      messages => {
        this.store.add(messages);
        this.updateDaysRead(messages);

        this.store.update({ page: page + 1, hasMore: messages.length > 0 });
        this.store.setLoading(false);
      },
      err => console.log('Error in MissionLogBookService.getForPage', err)
    );
  }

  // TODO: Does not yet filter! Does not yet work in the messages component if we only load alerts here
  getMaxItems(maxItems: number, typeFilter: string) {

    const options: HttpOptions = {
      params: {
          q: `sender.type==${typeFilter}`,
          type: 'LogBookEntry'
      }
    };
    this.missionLogBookHttpService.getMaxItems(maxItems).subscribe((messages) => {
      this.store.add(messages);
      this.updateDaysRead(messages);
    });
  }

  getForMissionId(missionId: string) {
    return this.missionLogBookHttpService.executeQuery(`missionId==${missionId}`, 'LogBookEntry').subscribe(
      (messages) => {
        this.store.add(messages);
        this.updateDaysRead(messages);
      });
  }

  markAllAsRead() {
    this.store.ui.update(null, { isRead: true, dateRead: new Date(), daysNotRead: null });
  }

  updateIsRead(id: ID, isRead: boolean) {
    this.store.ui.update(id, { isRead, dateRead: new Date(), daysNotRead: null });
  }

  addToHub(logBook: LogBookEntry) {
    this.store.add(logBook);

    const options: HttpOptions = {};

    const copy = { ...logBook };
    delete copy.dateIssuedDate;

    this.missionLogBookHttpService.update(copy, UpdateActionType.APPEND, options).subscribe(
      (data) => console.log('Updated logBook', data),
      (error) => console.log('Error inserting in Hub', error)
    );
  }

  private updateDaysRead(messages: LogBookEntry[]) {
    for (const msg of messages) {
      const dateSentDate = new Date(msg.dateIssued.value);
      const ui = this.store.ui.getValue().entities[msg.id];
      if (!ui?.dateRead) {
        const daysNotRead = differenceInDays(dateSentDate, new Date());
        this.store.ui.update(msg.id, {
          daysNotRead,
        });
      }
    }
  }
}
